import { useEffect, useState } from 'react';
import { UserProfile } from '../types/auth';
import { authApi, AuthUser } from '../api/auth';
import { getToken, clearToken, ApiError } from '../api/client';

// Minimal User type (was @supabase/supabase-js User; we keep shape for downstream consumers)
export interface User {
  id: string;
  email: string;
  app_metadata: Record<string, any>;
  user_metadata: Record<string, any>;
  aud: string;
  created_at: string;
}

function toMockUser(u: AuthUser): User {
  return {
    id: String(u.id),
    email: u.email,
    app_metadata: {},
    user_metadata: { role: u.role || '' },
    aud: 'authenticated',
    created_at: new Date().toISOString(),
  } as User;
}

function toUserProfile(u: AuthUser): UserProfile {
  return {
    id: String(u.id),
    name: u.name,
    email: u.email,
    role: u.role || '',
    status: u.status || 'active',
    access: u.access || [],
  };
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const restore = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const me = await authApi.me();
        setUser(toMockUser(me));
        setUserProfile(toUserProfile(me));
      } catch (e) {
        // Token invalid/expired
        clearToken();
        // Also clean legacy key
        localStorage.removeItem('crm_user_id');
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);
      const res = await authApi.login(email, password);
      setUser(toMockUser(res.user));
      setUserProfile(toUserProfile(res.user));
      return { success: true };
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Login failed');
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    clearToken();
    localStorage.removeItem('crm_user_id'); // legacy
    setUser(null);
    setUserProfile(null);
  };

  return { user, userProfile, loading, login, signOut };
}
